import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  HostListener,
  input,
  linkedSignal,
  OnDestroy,
  output,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { ButtonAtomComponent } from '@shared/atoms/button-atom/button-atom.component';

import {
  CatalogFilterOptions,
  CatalogFilterSelection,
  catalogFilterKey,
  countProductsInTree,
  filterTreeByCatalogFilters,
  type CatalogFilterCategoryNode,
  type CatalogFilterProduct,
} from './catalog-filters';

@Component({
  selector: 'app-shop-catalog-filters-dialog',
  imports: [NgClass, NgTemplateOutlet, TranslatePipe, ButtonAtomComponent],
  templateUrl: './shop-catalog-filters-dialog.component.html',
})
export class ShopCatalogFiltersDialogComponent implements OnDestroy {
  /** Kiosk always opens a centered dialog. Webapp stays a bottom sheet below the `lg` breakpoint. */
  readonly centered = input(false);
  readonly options = input.required<CatalogFilterOptions>();
  readonly categories = input.required<CatalogFilterCategoryNode<CatalogFilterProduct>[]>();
  readonly selection = input.required<CatalogFilterSelection[]>();

  readonly applied = output<CatalogFilterSelection[]>();
  readonly dismissed = output<void>();

  protected readonly draft = linkedSignal(() => [...this.selection()]);

  protected readonly visibleCount = computed(() =>
    countProductsInTree(filterTreeByCatalogFilters(this.categories(), this.draft())),
  );

  constructor() {
    document.body.classList.add('overflow-hidden');
  }

  ngOnDestroy(): void {
    document.body.classList.remove('overflow-hidden');
  }

  @HostListener('document:keydown.escape')
  protected dismiss(): void {
    this.dismissed.emit();
  }

  protected isSelected(option: CatalogFilterSelection): boolean {
    const key = catalogFilterKey(option);
    return this.draft().some(filter => catalogFilterKey(filter) === key);
  }

  protected toggle(option: CatalogFilterSelection): void {
    const key = catalogFilterKey(option);
    this.draft.update(current =>
      current.some(filter => catalogFilterKey(filter) === key)
        ? current.filter(filter => catalogFilterKey(filter) !== key)
        : [...current, option],
    );
  }

  protected clearDraft(): void {
    this.draft.set([]);
  }

  protected apply(): void {
    this.applied.emit(this.draft());
  }

  protected optionKey(option: CatalogFilterSelection): string {
    return catalogFilterKey(option);
  }
}
